import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { auth, googleProvider } from '../services/firebase';
import { signInWithPopup, signInAnonymously, signInWithEmailAndPassword, createUserWithEmailAndPassword, updateProfile } from 'firebase/auth';
import { Droplets, ShieldCheck, User, Mail, Lock, ArrowRight, Loader } from 'lucide-react';

export default function AuthScreen() {
    const [email, setEmail] = useState('');
    const [name, setName] = useState('');
    const [password, setPassword] = useState('');
    const [isRegistering, setIsRegistering] = useState(false);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    const handleGoogle = async () => {
        setLoading(true);
        try {
            await signInWithPopup(auth, googleProvider);
        } catch (e) {
            setError(e.message);
            setLoading(false);
        }
    };



    const handleEmailAuth = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);
        try {
            if (isRegistering) {
                const userCredential = await createUserWithEmailAndPassword(auth, email, password);
                if (name) {
                    await updateProfile(userCredential.user, { displayName: name });
                }
            } else {
                await signInWithEmailAndPassword(auth, email, password);
            }
        } catch (err) {
            setLoading(false);
            if (err.code === 'auth/user-not-found' && !isRegistering) {
                setError('Usuario no encontrado.');
            } else if (err.code === 'auth/wrong-password') {
                setError('Contraseña incorrecta.');
            } else {
                setError(err.message);
            }
        }
    };

    return (
        <div className="auth-screen">
            {/* Background Animation Blob */}
            <motion.div
                animate={{
                    scale: [1, 1.2, 1],
                    rotate: [0, 90, 0],
                }}
                transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                style={{
                    position: 'absolute',
                    width: '600px',
                    height: '600px',
                    borderRadius: '50%',
                    background: 'radial-gradient(circle, rgba(59,130,246,0.15) 0%, rgba(0,0,0,0) 70%)',
                    zIndex: 0,
                    top: '-200px',
                    left: '-200px'
                }}
            />

            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="auth-container glass-effect"
                style={{ zIndex: 1, position: 'relative', overflow: 'hidden' }}
            >
                {loading && (
                    <div style={{
                        position: 'absolute', inset: 0, background: 'rgba(15, 23, 42, 0.8)',
                        zIndex: 10, display: 'flex', alignItems: 'center', justifyContent: 'center',
                        backdropFilter: 'blur(4px)'
                    }}>
                        <motion.div animate={{ rotate: 360 }} transition={{ repeat: Infinity, duration: 1 }}>
                            <Loader size={40} color="#3b82f6" />
                        </motion.div>
                    </div>
                )}

                <div className="auth-header">
                    <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ type: 'spring', stiffness: 200, damping: 10 }}
                        style={{
                            background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                            width: 80, height: 80, borderRadius: 20, display: 'flex',
                            alignItems: 'center', justifyContent: 'center', margin: '0 auto 1.5rem',
                            boxShadow: '0 10px 25px -5px rgba(59, 130, 246, 0.5)'
                        }}
                    >
                        <span style={{ fontSize: '3rem' }}>🌊</span>
                    </motion.div>
                    <h1>Floppy Floods</h1>
                    <p style={{ color: '#94a3b8' }}>Sistema de Alerta Temprana</p>
                </div>

                <div className="auth-body">
                    <div style={{ display: 'grid', gap: 10, marginBottom: 20 }}>
                        <button onClick={handleGoogle} className="btn full-width" style={{ background: 'white', color: '#334155', border: '1px solid #e2e8f0' }}>
                            <img src="https://www.google.com/favicon.ico" alt="G" style={{ width: 18, height: 18 }} />
                            Continuar con Google
                        </button>

                    </div>

                    <div className="auth-divider" style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '20px 0', color: '#64748b' }}>
                        <div style={{ height: 1, background: '#334155', flex: 1 }}></div>
                        <span style={{ fontSize: '0.8rem' }}>o con correo</span>
                        <div style={{ height: 1, background: '#334155', flex: 1 }}></div>
                    </div>

                    <form onSubmit={handleEmailAuth}>
                        {isRegistering && (
                            <div className="input-group" style={{ position: 'relative', marginBottom: 15 }}>
                                <User size={18} style={{ position: 'absolute', left: 15, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
                                <input
                                    type="text"
                                    placeholder="Tu Nombre Completo"
                                    value={name}
                                    onChange={e => setName(e.target.value)}
                                    required={isRegistering}
                                    style={{ paddingLeft: 45 }}
                                    className="input-text"
                                />
                            </div>
                        )}
                        <div className="input-group" style={{ position: 'relative', marginBottom: 15 }}>
                            <Mail size={18} style={{ position: 'absolute', left: 15, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
                            <input
                                type="email"
                                placeholder="nombre@correo.com"
                                value={email}
                                onChange={e => setEmail(e.target.value)}
                                required
                                style={{ paddingLeft: 45 }}
                                className="input-text"
                            />
                        </div>
                        <div className="input-group" style={{ position: 'relative', marginBottom: 20 }}>
                            <Lock size={18} style={{ position: 'absolute', left: 15, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
                            <input
                                type="password"
                                placeholder="Tu contraseña segura"
                                value={password}
                                onChange={e => setPassword(e.target.value)}
                                required
                                style={{ paddingLeft: 45 }}
                                className="input-text"
                            />
                        </div>

                        <AnimatePresence>
                            {error && (
                                <motion.div
                                    initial={{ opacity: 0, height: 0 }}
                                    animate={{ opacity: 1, height: 'auto' }}
                                    exit={{ opacity: 0, height: 0 }}
                                    className="error-banner"
                                    style={{
                                        background: 'rgba(239, 68, 68, 0.1)',
                                        border: '1px solid rgba(239, 68, 68, 0.2)',
                                        color: '#f87171', padding: 10, borderRadius: 8, marginBottom: 15, fontSize: '0.9rem'
                                    }}
                                >
                                    {error}
                                </motion.div>
                            )}
                        </AnimatePresence>

                        <button type="submit" className="btn btn-primary full-width" style={{ justifyContent: 'space-between', paddingRight: 20 }}>
                            <span>{isRegistering ? 'Crear Cuenta' : 'Iniciar Sesión'}</span>
                            <ArrowRight size={18} />
                        </button>
                    </form>

                    <div style={{ textAlign: 'center', marginTop: 15 }}>
                        <button
                            onClick={() => { setIsRegistering(!isRegistering); setError(''); }}
                            style={{ background: 'none', border: 'none', color: '#60a5fa', cursor: 'pointer', fontSize: '0.9rem' }}
                        >
                            {isRegistering ? '¿Ya tienes cuenta? Ingresa aquí' : '¿No tienes cuenta? Regístrate gratis'}
                        </button>
                    </div>
                </div>
            </motion.div>
        </div>
    );
}
